import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Form, Item, RequiredRule, Label } from "devextreme-react/form";
import { Button } from "devextreme-react/button";
import authService from "../services/authService";
import { useAuth } from "../context/AuthContext";
import "../style/SignIn.scss";

const logo2 = "/icons/ComputAssist Badge.png";

const SignIn = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const [formData, setFormData] = useState({
    username: "",
    password: "",
    rememberMe: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState("LOGIN"); // LOGIN | CONFLICT

  // Restore remembered username
  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("rememberMe") || "{}");
    if (saved.username) {
      setFormData((prev) => ({
        ...prev,
        username: saved.username,
        rememberMe: true,
      }));
    }
  }, []);

  const handleSuccessfulLogin = (response) => {
    if (formData.rememberMe) {
      localStorage.setItem(
        "rememberMe",
        JSON.stringify({ username: formData.username })
      );
    } else {
      localStorage.removeItem("rememberMe");
    }

    setUser(response.currentUser);
    navigate("/", { replace: true });
  };

  const onLogin = async (event, force = false) => {
    event?.preventDefault();

    setError("");
    setLoading(true);

    try {
      const response = await authService.login(
        formData.username,
        formData.password,
        force
      );

      if (response.status === "Success") {
        handleSuccessfulLogin(response);
        return;
      }

      if (response.status === "SessionConflict") {
        setStep("CONFLICT");
        return;
      }

      setError(response.error || "Authentication failed.");
    } catch (err) {
      setError(err.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const LoginForm = () => (
    <form onSubmit={onLogin}>
      <Form
        formData={formData}
        labelLocation="top"
        disabled={loading}
        onFieldDataChanged={(e) =>
          setFormData((prev) => ({
            ...prev,
            [e.dataField]: e.value,
          }))
        }
      >
        <Item
          dataField="username"
          editorOptions={{
            placeholder: "Username",
            inputAttr: { autoComplete: "username" },
          }}
        >
          <RequiredRule />
          <Label text="Username" />
        </Item>

        <Item
          dataField="password"
          editorOptions={{
            mode: "password",
            placeholder: "Password",
            inputAttr: { autoComplete: "current-password" },
          }}
        >
          <RequiredRule />
          <Label text="Password" />
        </Item>

        <Item
          dataField="rememberMe"
          editorType="dxCheckBox"
          editorOptions={{ text: "Remember Me" }}
        >
          <Label visible={false} />
        </Item>

        <Item>
          <Button
            width="100%"
            text={loading ? "Logging in..." : "Login"}
            type="default"
            useSubmitBehavior
            disabled={loading}
          />
        </Item>
      </Form>

      <div className="reset-link">
        <Link to="/forgot-password">Forgot password?</Link>
      </div>
    </form>
  );

  const ConflictView = () => (
    <div className="login-step">
      <h3>Active Session</h3>
      <p>
        You are already logged in elsewhere. Do you want to switch this session
        to this device?
      </p>
      <div className="step-actions">
        <Button
          text={loading ? "Switching..." : "Yes, Switch"}
          type="default"
          onClick={() => onLogin(null, true)}
          disabled={loading}
        />
        <Button
          text="Cancel"
          onClick={() => {
            setError("");
            setStep("LOGIN");
          }}
          disabled={loading}
        />
      </div>
    </div>
  );

  return (
    <div className="login">
      <div className="login-form">
        <img src={logo2} alt="ComputAssist" />

        {step === "LOGIN" && <LoginForm />}
        {step === "CONFLICT" && <ConflictView />}

        {error && <div className="error-message">{error}</div>}
      </div>
    </div>
  );
};

export default SignIn;